import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import * as S from './Ads.styles';
import { Footer } from '@/components/Footer/Footer';
import { TabBar } from '@/components/TabBar/TabBar';
import { PageTopControls } from '@/components/PageTopControls';
import { SearchBaseResults } from '@/components/SearchBaseResults/SearchBaseResults';
import { useCurrentCategory } from '@/hooks/useCurrentCategory/useCurrentCategory';
import { useBanner } from '@/hooks/useBanner';
import { SearchPageSkeleton } from '@/components/AdsWithInfiniteScroll/SearchPageSkeleton';
import { CatalogueGrid } from '@/components/CatalogueGrid/CatalogueGrid';
import { SearchPanel } from '@/components/SearchPanel';
import { FiltersButton } from '@/components/Filters/FiltersButton/FiltersButton';
import { usePageTopControlsStore } from '@/store/usePageTopControlsStore';
import { useServerDataContext } from '@/contexts';
import { InlineFiltersSkeletonLoader } from '@/components/Filters/InlineFilters/InlineFiltersSkeletonLoader';
import { useResponsive } from '@/hooks/useResponsive';
import { DesktopFooter } from '@/components/Footer';
import {
  SearchResults,
  SearchResultsSections,
} from '@/components/SearchResults';

const VIP_ONLY_SECTIONS: SearchResultsSections = {
  vipAds: true,
  latestAds: false,
};
const VIP_AND_LATEST_SECTIONS: SearchResultsSections = {
  vipAds: true,
  latestAds: true,
};

const UserAdsWithInfiniteScroll = dynamic(
  () =>
    import('@/components/AdsWithInfiniteScroll/UserAdsWithInfiniteScroll').then(
      mod => mod.UserAdsWithInfiniteScroll
    ),
  {
    ssr: false,
    loading: () => <SearchPageSkeleton />,
  }
);

const InlineFilters = dynamic(
  () =>
    import('@/components/Filters/InlineFilters').then(mod => mod.InlineFilters),
  {
    ssr: false,
    loading: () => <InlineFiltersSkeletonLoader />,
  }
);

export const AdsView = () => {
  const { t } = useTranslation();
  const { currentCategory: serverCurrentCategory } = useServerDataContext();
  const { isDesktop } = useResponsive();
  const {
    isVipAds,
    isAllCategories,
    isFirstLevel,
    isSubcategories,
    currentCategory = serverCurrentCategory,
    isUserAds,
  } = useCurrentCategory();
  const { topBanner } = useBanner({ isDesktop });
  const hasCategories = (isAllCategories || isFirstLevel) && !isVipAds;
  const topControlsPosition = usePageTopControlsStore(
    state => state.topControlsPosition
  );
  const [hasNextPage, setHasNextPage] = useState(true);

  const handlePaginationChange = useCallback((nextPageExists: boolean) => {
    setHasNextPage(nextPageExists);
  }, []);

  function getHeaderTitle() {
    if (isVipAds) {
      return t('pages.ads.all_vip_ads');
    }

    if (isFirstLevel || isSubcategories) {
      return currentCategory?.name;
    }

    return t('pages.ads.all_categories');
  }

  function renderAdsList() {
    switch (true) {
      case isVipAds: {
        return <SearchResults sections={VIP_ONLY_SECTIONS} />;
      }
      case isUserAds: {
        return <UserAdsWithInfiniteScroll />;
      }
      default: {
        return (
          <SearchResults
            sections={VIP_AND_LATEST_SECTIONS}
            onPaginationChange={handlePaginationChange}
          />
        );
      }
    }
  }

  const isTopControlsFixed = topControlsPosition === 'fixed';
  const isFooterVisible = isUserAds || !hasNextPage;

  return (
    <>
      {topBanner}

      <PageTopControls>
        <S.Header
          title={getHeaderTitle()}
          content={!isDesktop && <SearchPanel />}
          rightAction={<FiltersButton />}
        />

        {!isVipAds && !isDesktop && (
          <SearchBaseResults isHidden={isTopControlsFixed} />
        )}

        {hasCategories && !isDesktop && (
          <CatalogueGrid isHidden={isTopControlsFixed} />
        )}

        {!isDesktop && <InlineFilters isSortVisible={!isTopControlsFixed} />}
      </PageTopControls>

      <S.Wrapper>
        <S.Container>
          {!isVipAds && isDesktop && <SearchBaseResults />}

          {hasCategories && isDesktop && <CatalogueGrid />}

          {renderAdsList()}
        </S.Container>
      </S.Wrapper>

      <S.FooterBox $isVisible={isFooterVisible}>
        {isDesktop ? <DesktopFooter /> : <Footer />}
      </S.FooterBox>

      {!isDesktop && <TabBar />}
    </>
  );
};
